/**
 * PostHog implementation of AnalyticsClient (frontend)
 *
 * Wraps posthog-js to implement the provider-agnostic AnalyticsClient interface.
 */

import type {
	AnalyticsClient,
	AnalyticsGroupProperties,
	AnalyticsGroupType,
	AnalyticsUserProperties,
} from '../analytics/types';
import type { AnalyticsEvents, AnalyticsEventName } from '../analytics/events';

import { initPostHog, isDevMode, withPostHog } from './client';

export const posthogAnalyticsClient: AnalyticsClient = {
	init: () => {
		initPostHog();
	},

	track: <T extends AnalyticsEventName>(
		eventName: T,
		properties: AnalyticsEvents[T]
	): void => {
		if (process.env.NODE_ENV === 'development') {
			console.log(`[Analytics] ${eventName}`, properties);
		}
		if (!isDevMode()) {
			// Queued and replayed if posthog-js is still loading, so an event
			// fired during boot (Tier-1 dispatch included) is never dropped.
			withPostHog((posthog) => posthog.capture(eventName, properties));
		}
	},

	identify: (userId: string, properties?: AnalyticsUserProperties): void => {
		if (process.env.NODE_ENV === 'development') {
			console.log('[Analytics] identify', { userId, ...properties });
		}
		if (!isDevMode()) {
			withPostHog((posthog) => posthog.identify(userId, properties));
		}
	},

	setPersonProperties: (properties: AnalyticsUserProperties): void => {
		if (process.env.NODE_ENV === 'development') {
			console.log('[Analytics] setPersonProperties', properties);
		}
		if (!isDevMode()) {
			// `$set` on the current person. Like capture(), it is a no-op while
			// the user is opted out, so the consent gate covers it with no extra
			// check (see deps/posthog/client.ts).
			withPostHog((posthog) => posthog.setPersonProperties(properties));
		}
	},

	group: (
		groupType: AnalyticsGroupType,
		groupKey: string,
		properties?: AnalyticsGroupProperties
	): void => {
		if (process.env.NODE_ENV === 'development') {
			console.log('[Analytics] group', { groupType, groupKey, ...properties });
		}
		if (!isDevMode()) {
			// posthog.group() both captures $groupidentify and attaches $groups to
			// every subsequent event — and, like capture(), it is a no-op while the
			// user is opted out, so the consent gate covers it with no extra check.
			withPostHog((posthog) => posthog.group(groupType, groupKey, properties));
		}
	},

	reset: (): void => {
		if (process.env.NODE_ENV === 'development') {
			console.log('[Analytics] reset user');
		}
		if (!isDevMode()) {
			withPostHog((posthog) => posthog.reset());
		}
	},
};
